import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { StandardURL } from "../App";


const RentalsPage = () => {
  const [propertyId, setPropertyId] = useState("");
  const [rentals, setRentals] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [rentalIdToUpdate, setRentalIdToUpdate] = useState("");
  const [rentPriceInput, setRentPriceInput] = useState("");

  useEffect(() => {
    if (propertyId) {
      fetchRentals();
    }
  }, [propertyId]);

  // Helper function to get token
  const getToken = () => localStorage.getItem("token");

  // Fetch rentals for the selected property
  const fetchRentals = async () => {
    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/admin/rentals/propertyall/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      setRentals(response.data.response);
    } catch (error) {
      handleError(error, "Error fetching rentals");
    }
  };

  // Handle rental deletion
  const handleDelete = async (rentalId) => {
    try {
      const response = await axios.delete(
        `${StandardURL}/iNyumba/admin/rentals/delete/${rentalId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      toast.success(response.data.response);
      fetchRentals();
    } catch (error) {
      handleError(error, "Error deleting rental");
    }
  };

  // Handle rental price update
  const handleUpdate = async () => {
    try {
      const response = await axios.patch(
        `${StandardURL}/iNyumba/admin/rentals/price/${rentalIdToUpdate}`,
        { rent_price: rentPriceInput },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      toast.success(response.data.response);
      setModalOpen(false);
      fetchRentals();
    } catch (error) {
      handleError(error, "Error updating rental price");
    }
  };

  // Open modal to update rental price
  const openModalToUpdate = (rentalId) => {
    setRentalIdToUpdate(rentalId);
    setModalOpen(true);
  };

  // Handle errors with a toast notification
  const handleError = (error, defaultMessage) => {
    console.error(defaultMessage, error);
    const errorMessage = error.response?.data?.response || defaultMessage;
    toast.error(errorMessage);
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl rounded-2xl bg-white p-8 shadow-xl">
            <h1 className="mb-6 text-3xl font-bold tracking-tight text-gray-900">
              Rental Management
            </h1>
            <div className="mb-6 flex items-center space-x-4">
              <input
                type="text"
                placeholder="Enter Property ID"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                className="flex-grow rounded-lg border border-gray-300 px-4 py-2 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={fetchRentals}
                className="transform rounded-full bg-blue-600 px-6 py-3 font-semibold text-white shadow-md transition-all duration-200 hover:scale-105 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Fetch Rentals
              </button>
            </div>

            <div className="overflow-x-auto rounded-lg shadow-md">
              <table className="min-w-full table-auto text-sm">
                <thead className="bg-gray-100">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    <th className="px-6 py-4">Rental ID</th>
                    <th className="px-6 py-4">Rent Price</th>
                    <th className="px-6 py-4">House Number</th>
                    <th className="px-6 py-4">House Type</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {rentals.map((rental) => (
                    <tr
                      key={rental.id}
                      className="transition-colors duration-200 hover:bg-sky-50"
                    >
                      <td className="whitespace-nowrap px-6 py-4 font-medium text-gray-900">{rental.rental_id}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-gray-700">KES {rental.rent_price}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-gray-700">{rental.house_number}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-gray-700">{rental.house_type}</td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                          rental.status === "Occupied"
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                        }`}>
                          {rental.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => handleDelete(rental.rental_id)}
                            className="transform rounded-full bg-red-500 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all duration-200 hover:scale-105 hover:bg-red-600"
                          >
                            Delete
                          </button>
                          <button
                            onClick={() => openModalToUpdate(rental.rental_id)}
                            className="transform rounded-full bg-blue-500 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all duration-200 hover:scale-105 hover:bg-blue-600"
                          >
                            Update
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Modal for updating rent price */}
            {modalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm">
                <div className="w-full max-w-sm transform rounded-2xl bg-white p-8 shadow-2xl">
                  <h2 className="mb-4 text-xl font-bold text-gray-800">
                    Update Rent Price
                  </h2>
                  <input
                    type="number"
                    value={rentPriceInput}
                    onChange={(e) => setRentPriceInput(e.target.value)}
                    className="mb-6 w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter new rent price"
                  />
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={handleUpdate}
                      className="transform rounded-full bg-blue-600 px-6 py-2 font-semibold text-white shadow-sm transition-all duration-200 hover:scale-105 hover:bg-blue-700"
                    >
                      Update
                    </button>
                    <button
                      onClick={() => setModalOpen(false)}
                      className="transform rounded-full bg-gray-400 px-6 py-2 font-semibold text-white shadow-sm transition-all duration-200 hover:scale-105 hover:bg-gray-500"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default RentalsPage;
